import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  getinitNotificationFeatures,
  getinitUserControlFeatures,
} from "./helper/UserSliceHelper";
import { DefineUserFeatures } from "./handlers/AppFeaturesHandler";
import { SSO_ROLE_CONSTANT } from "../components/Mangement/modal/CreateAndEditNotify/FormDataConstants";
import {
  CApplicationID,
  CAPPNames,
  CPartnerType,
} from "../components/Constants/Application";
import {
  GetPartnerType,
  GetPartnerTypeForOPF,
} from "../api/ssoapi/UserControlapi";
import {
  getApiFailState,
  getApiInitState,
  getApiLoadingState,
  getApiSuccessState,
} from "../api/notifyapi/APIShared";
import { IAPIState } from "../api/notifyapi/Notifyapi";

export interface ISSOUser {
  userName: string;
  email: string;
  accessApplications: ISSOUserApplicationAccess[];
  isAdmin: boolean;
  isDiscoverUser: boolean;
  lastLogin: string;
  status: string;
  createdOn: string;
  updatedOn: string;
  lastUsedApplicationId: string;
  isOtpEnabled: boolean;
  authentication: ISSOAuthentication;
  isSSOLoggedIn: boolean;
  ccEmailIds: string[];
  ssoRoles: string[];
}

export interface ISSOUserApplicationAccess {
  applicationID: string;
  state: string;
  isMailSent: boolean;
}

export interface ISSOAuthentication {
  keyType: string;
  state: string;
}

export interface IApplicationSetup {
  id: string;
  name: string;
  image: string;
  platform: string;
  displayOrder: number;
  status: number;
  displayState: string;
  createdOn: string;
  lastUpdatedOn: string;
  setupUrl: string;
  //public AuthentificationConfig AuthConfig { get; set; } = new AuthentificationConfig();
  //public string? APIClass { get; set; }
  //public Dictionary<string, string> JWTConfigurations { get; set; } = new Dictionary<string, string>();
  clientId: string;
}

export interface IAppFeatures {
  notification: INotificationFeatures;
  userControl: IUserControlFeatures;
}

export interface INotificationFeatures {
  accessNotificationBoard: boolean;
  viewNotify: boolean;
  createNotify: boolean;
  editNotify: boolean;
  deleteNotify: boolean;
  submitNotify: {
    level1: boolean;
    level2: boolean;
  };
  assignNotify: {
    level1: boolean;
    level2: boolean;
  };
  publishNotify: {
    level1: boolean;
    level2: boolean;
  };
  rejectNotify: {
    level1: boolean;
    level2: boolean;
  };
  recallNotify: {
    level1: boolean;
    level2: boolean;
  };
}

export interface IUserControlFeatures {
  viewUserControl: boolean;
}

export interface ISwitchRole {
  accessRoles: string[];
  activeRole: string;
}

interface IUserSliceState {
  api: IAPIState;
  accessApplications: string[];
  user: ISSOUser | null;
  applications: IApplicationSetup[];
  features: IAppFeatures;
  switchRole: ISwitchRole;
  partner: IPartnerType;
}

export interface IPartnerType {
  isPartner: boolean;
  partnerType: string[];
  emailId: string;
  accessRoles: string[];
}

export interface IPartnerTypeResponse {
  partnerType: string;
  emailId: string;
  accessRoles: string[];
}

const initialState: IUserSliceState = {
  api: getApiInitState(),
  user: null,
  applications: [],
  accessApplications: [],
  partner: { emailId: "", partnerType: [], accessRoles: [], isPartner: false },
  features: {
    notification: getinitNotificationFeatures(),
    userControl: getinitUserControlFeatures(),
  },
  switchRole: {
    accessRoles: [],
    activeRole: "",
  },
};

export const UserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    UpdateUser: (state, action: PayloadAction<{ user: ISSOUser }>) => {
      //Update the user roles
      state.switchRole.accessRoles = action.payload.user.ssoRoles;
      state.accessApplications = handleGetPartnerTypeAPI(action.payload?.user);
      // conditon for if user have one accessRole set the active role
      if (state.switchRole.accessRoles.length === 1) {
        state.switchRole.activeRole = state.switchRole.accessRoles[0];
      }
      // condition for user have multuple access set initial active role as admin
      else if (state.switchRole.accessRoles.length > 1) {
        if (state.switchRole.accessRoles.includes(SSO_ROLE_CONSTANT.admin)) {
          state.switchRole.activeRole = SSO_ROLE_CONSTANT.admin;
        }
        // other wise select the 1st role ass active role
        else {
          state.switchRole.activeRole = state.switchRole.accessRoles[0];
        }
      } else {
        state.switchRole.activeRole = "";
      }

      state.features = DefineUserFeatures(
        action.payload.user,
        state.switchRole.activeRole
      );

      state.user = action.payload.user;
      action.payload?.user.accessApplications.map(
        (access: ISSOUserApplicationAccess) => {
          if (
            access.applicationID === CApplicationID.CTC &&
            !action.payload.user.email.includes("@discover.com")
          ) {
            state.partner.partnerType = [
              ...state.partner.partnerType,
              CPartnerType.CTC_PARTNER_ACQ,
            ];
            state.partner.emailId = action.payload.user.email;
            state.partner.isPartner = true;
          } else if (access.applicationID === CApplicationID.REPORT) {
          } else if (access.applicationID === CApplicationID.SSO) {
          }
        }
      );
      //return { ...state, user: { ...state.user, ...action.payload.user } };
    },
    UpdateApplications: (
      state,
      action: PayloadAction<{ applications: IApplicationSetup[] }>
    ) => {
      state.applications = action.payload.applications;
      //return { ...state, applications: { ...state.user, ...action.payload.user } };
    },
    UpdateActiveRole: (
      state,
      action: PayloadAction<{ activeRole: string }>
    ) => {
      state.switchRole.activeRole = action.payload.activeRole;
      state.features = DefineUserFeatures(
        state.user,
        state.switchRole.activeRole
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetPartnerType.pending, (state, action) => {
        state.api = getApiLoadingState();
        state.partner.isPartner = false;
      })
      .addCase(
        GetPartnerType.fulfilled,
        (state, action: PayloadAction<IPartnerTypeResponse>) => {
          state.api = getApiSuccessState();

          if (action?.payload?.partnerType) {
            state.partner.accessRoles = action.payload.accessRoles;
            state.partner.partnerType = [
              ...state.partner.partnerType,
              "CCT_" + action.payload.partnerType,
            ];
            state.partner.isPartner = true;
          }
        }
      )
      .addCase(GetPartnerType.rejected, (state, action) => {
        state.api = getApiFailState();
        state.partner.isPartner = false;
      })
      .addCase(GetPartnerTypeForOPF.pending, (state, action) => {
        state.api = getApiLoadingState();
        state.partner.isPartner = false;
      })
      .addCase(GetPartnerTypeForOPF.fulfilled, (state, action: any) => {
        state.api = getApiSuccessState();
        console.log(action?.payload);

        if (action?.payload?.partnerType) {
          state.partner.isPartner = true;
          console.log(action.payload);

          state.partner.partnerType = [
            ...state.partner.partnerType,
            "OPF_" + action.payload.partnerType.toUpperCase(),
          ];
        } else {
          console.warn("Payload or partnerType is null/undefined");
          state.partner.isPartner = false;
        }
      })
      .addCase(GetPartnerTypeForOPF.rejected, (state, action) => {
        state.api = getApiFailState();
        state.partner.isPartner = false;
      });

  },
});

export const { UpdateUser, UpdateApplications, UpdateActiveRole } =
  UserSlice.actions;
export default UserSlice.reducer;

export const handleGetPartnerTypeAPI = (user: ISSOUser) => {
  var result: string[] = [];
  user?.accessApplications?.map((access) => {
    if (access.applicationID == CApplicationID.CCT) {
      result.push(CAPPNames.CCT);
    }
    if (access.applicationID == CApplicationID.CTC) {
      result.push(CAPPNames.CTC);
    }
    if (access.applicationID == CApplicationID.REPORT) {
      result.push(CAPPNames.REPORT);
    }
    if (access.applicationID == CApplicationID.OPF) {
      result.push(CAPPNames.OPF);
    }
    if (access.applicationID == CApplicationID.SSO) {
      result.push(CAPPNames.SSO);
    }
  });
  return result;
};
